import Loqate from 'react-loqate';
import React, { useState, useEffect } from 'react';
import { CLIENT_TYPE_OPTIONS, VALID_COUNTRIES, cn } from '@/lib/utils';
import { useForm, router, usePage, Head } from '@inertiajs/react';
import { validateEmail, validatePostcode, validatePhoneUK, validationMessages } from '@/lib/utils/validate';
import SimpleHeaderLayout from '@/Layouts/SimpleHeaderLayout';
import Heading from '@/components/atoms/heading';
import ActionFooter from '@/components/molecules/actionFooter';
import NotificationCard from '@/components/molecules/notificationCard';
import ProgressBar from '@/components/atoms/ProgressBar';
import FrontHeaderLayout from '@/Layouts/FrontHeaderLayout';
import FormHeaderTitle from '@/components/molecules/formHeaderTitle';
import { axiosHandler } from '@/lib/utils/axiosHandler';
import StepOne from './Steps/StepOne';
import StepTwo from './Steps/StepTwo';

export default function AddClientForm({ client, handleCloseBtn, auth, submitDestination = 'modal', policyType = null }) {
	const { accountType } = usePage().props.auth;
	const isEIS = accountType === 'eis';
	const isGuest = accountType === 'guest';

	const [formStep, setFormStep] = useState(1);
	const [hasDuplicates, setHasDuplicates] = useState(false);
	const [ignoreDuplicates, setIgnoreDuplicates] = useState(false);
	const [duplicateData, setDuplicateData] = useState([]);
	const [showAlert, setShowAlert] = useState(false);
	const [inputTouched, setInputTouched] = useState({
		firstNameTouched: false,
		lastNameTouched: false,
		emailTouched: false,
		telephoneNumTouched: false,
	});

	const isClientBrokerRequired = isGuest ? ['is_client_broker'] : [];

	const requiredList = ['first_name', 'last_name', 'age_check', 'email', ...isClientBrokerRequired];
	const firstStep = ['first_name', 'last_name', 'email', ...isClientBrokerRequired];
	const brokerRequiredList = firstStep.concat(['broker_id','broker_postcode','broker_name','broker_phone','broker_email']);

	const { data, setData, errors, setError, clearErrors, post } = useForm({
		client_id: null,
		first_name: client?.first_name || '',
		last_name: client?.last_name || '',
		email: client?.email || '',
		postcode: client?.postcode || '',
		browse_duplicates: client?.browse_duplicates || '',
		client_type: client?.client_type || '',
		age_check: client?.age_check || '',
		legal_title: client?.legal_title || '',
		title: client?.title || '',
		address_1: client?.address_1 || '',
		address_2: client?.address_2 || '',
		town: client?.town || '',
		county: client?.county || '',
		country: client?.country || '',
		telephone_num: client?.telephone_num || '',
		broker_id: client?.broker_id || '',
		introducer: client?.introducer || '',
		policy_type: policyType || '',
		is_business: client?.is_business || '',
		is_client_broker: client?.is_client_broker || '',
		broker_name: '',
		broker_phone: '',
		broker_email: '',
		broker_postcode: '',
	});

	// const getAllDropdowns = async () => {
	// 	const ltresponseData = await axiosHandler(auth, 'get', `/api/dropdown/legal_title`);
	// 	const tResponseData = await axiosHandler(auth, 'get', `/api/dropdown/title`);

	// 	setAllDropdowns((prev) => ({
	// 		...prev,
	// 		title: tResponseData,
	// 		legal_title: ltresponseData,
	// 	}));
	// };

	const setTouched = (itemName, bool) => {
		setInputTouched({ ...inputTouched, [itemName]: bool });
	};

	const getValidationType = (itemName) => {
		let validationType = 'required';

		switch (itemName) {
			case 'email':
				validationType = 'email';
				break;

			case 'postcode':
				validationType = 'postcode';
				break;

			case 'telephone_num':
				validationType = 'tel';
				break;

			default:
				break;
		}

		return validationType;
	};

	const validateFields = (fields) => {
		let isFormValid = true;

		const isClientBroker = data.is_client_broker === 'yes';
		const allFieldRequired = isClientBroker ? brokerRequiredList : firstStep;

		fields.forEach((itemName) => {
			const validationType = getValidationType(itemName);

			if (
				(allFieldRequired.includes(itemName) && !data[itemName]) ||
				(validationType === 'email' && !validateEmail(data[itemName])) ||
				(validationType === 'tel' && !validatePhoneUK(data[itemName])) ||
				(validationType === 'postcode' && data[itemName] && !validatePostcode(data[itemName]))
			) {
				setError(itemName, validationMessages[validationType]);
				setTouched(itemName, true);
				isFormValid = false;
			} else {
				clearErrors(itemName);
				setTouched(itemName, false);
			}
		});

		return isFormValid;
	};

	const handleOnChange = (itemName, value) => {
		setData(itemName, value);

		const isClientBroker = data.is_client_broker === 'yes';
		const allFieldRequired = isClientBroker ? brokerRequiredList : firstStep;

		const validationType = getValidationType(itemName);
		if (
			(allFieldRequired.includes(itemName) && !value) ||
			(validationType === 'email' && !validateEmail(value)) ||
			(validationType === 'tel' && !validatePhoneUK(value)) ||
			(validationType === 'postcode' && value && !validatePostcode(value))
		) {
			setError(itemName, validationMessages[validationType]);
		} else {
			clearErrors(itemName);
		}
	};

	const handleOnBlur = (itemName) => {
		validateFields([itemName]);
	};

	const StepSuccessThree = () => (
		<div className="flex flex-col items-center justify-center grow">
			<NotificationCard
				type="success"
				title="Client successfully created"
				titleClassName="normal-case"
				content="Now you have created a client, you can either view their details or create a policy for them."
				priBtn="New policy"
				secBtn="Go to client list"
				priBtnOnClick={() => {
					router.visit(route('admin.policies.show'));
				}}
				secBtnOnClick={() => {
					router.visit(route('admin.clients.show'));
				}}
			/>
		</div>
	);

	const submit = (e) => {
		e.preventDefault();

		post(route('clients.create'));
	};

	const priBtnOnClick = async () => {
		if (formStep === 1 && !ignoreDuplicates) {
			const isClientBroker = data.is_client_broker === 'yes';
			const isFormValid = validateFields(isClientBroker ? brokerRequiredList : requiredList);

			if (isFormValid) {
				setShowAlert(true);
				const useMagicLink = !isEIS ? { link: policyType } : {};
				const responseData = await axiosHandler(auth, 'get', route('admin.clients.check-for-duplicates'), {
					...data,
					...useMagicLink,
				});

				if (responseData.hasDuplicates) {
					setHasDuplicates(true);
				} else {
					setHasDuplicates(false);
					setFormStep(formStep + 1);
				}

				if (responseData.duplicates) {
					setDuplicateData(responseData.duplicates);
				}
			}
		} else {
			setFormStep(formStep + 1);
			setShowAlert(false);
		}

		// if (hasDuplicates && formStep < 2) {
		//setFormStep(formStep + 1);  //block if duplicatees found
		// }
	};

	const getProgressValue = (step) => {
		if (step === 1) {
			return 50;
		} else if (step === 2) {
			return 100;
		}
	};

	const handleBackBtn = (step) => {
		if (step === 1) {
			handleCloseBtn();
		} else {
			setFormStep(formStep - 1);
		}
	};

	const getPriBtnName = (step) => {
		if (step === 1) {
			if (isGuest) {
				return 'Next';
			}
			if (showAlert) {
				if (duplicateData.length) {
					if (!ignoreDuplicates) setIgnoreDuplicates(true);
					return 'Continue anyway';
				} else {
					return 'Continue';
				}
			} else {
				return 'Check for duplicates';
			}
		} else if (step === 2) {
			return !isEIS ? 'Next' : 'Add Client';
		} else if (step === 3) {
			return 'Close';
		}
	};

	const WrapComponent = !isEIS ? FrontHeaderLayout : SimpleHeaderLayout;
	let firstStepNoErrors =
		// data?.is_client_broker &&
		data.first_name &&
		data.last_name &&
		data.email &&
		(
			// errors?.is_client_broker || 
			errors?.first_name || errors?.last_name || errors?.email);

	// if(formStep === 1) {
	// 	if(!data?.is_client_broker) {

	// }

	let stepToShow = null;
	let headerSubtitle = '';

	switch (formStep) {
		case 1:
			stepToShow = (
				<StepOne
					data={data}
					errors={errors}
					showAlert={showAlert}
					duplicateData={duplicateData}
					hasDuplicates={hasDuplicates}
					handleOnChange={handleOnChange}
					handleOnBlur={handleOnBlur}
					setData={setData}
				/>
			);
			headerSubtitle = "Enter the Insured's details";
			break;
		case 2:
			stepToShow = <StepTwo data={data} errors={errors} showAlert={showAlert} handleOnChange={handleOnChange} handleOnBlur={handleOnBlur} setData={setData} />;
			headerSubtitle = 'Insured Policy Holder Details';
			break;
		case 3:
			stepToShow = <StepSuccessThree />;
	}
	let hideNext = false;
	if (data.country && !VALID_COUNTRIES.includes(data.country)) hideNext = true;
	if (!hideNext && data.age_check === 'no') hideNext = true;
	// if (!isEIS && hasDuplicates && formStep === 1) hideNext = true; // hide next button if duplicates found

	return (
		<WrapComponent
			btnText={formStep !== 1 && 'Back'}
			handleCloseBtn={handleCloseBtn}
			handleBackBtn={() => handleBackBtn(formStep)}
			headerTitle={'Insured Policy Holder Details'}
			stepTitle={headerSubtitle}
			headerTitleVariant="corporateBlue"
			pageTitle={'Insured Policy Holder Details'}
		>
			{isEIS && formStep !== 3 && (
				<FormHeaderTitle
					variant="corporateBlue"
					title={'Insured Policy Holder Details'}
					subtitle={headerSubtitle}
					className="mt-16"
					titleClassName="normal-case"
					subtitleClassName="normal-case"
				/>
			)}

			{isEIS && (formStep === 1 || formStep === 2) && <ProgressBar value={getProgressValue(formStep)} />}

			<div className={cn('flex flex-col w-full mx-auto grow max-w-eis-1440 px-6 md:px-10 lg:px-14 xl:px-eis-72', isEIS && 'pt-eis-55 pb-eis-73')}>
				<main className="flex flex-col grow">
					<div className="flex flex-col gap-8 py-8 grow">
						{!isEIS && formStep !== 3 && (
							<Heading className="text-corporateBlue" type="h3">
								Enter the Insured's details
							</Heading>
						)}
						<form className="flex flex-col grow">{stepToShow}</form>
					</div>
				</main>
			</div>
			<ActionFooter
				hidePrimary={hideNext}
				priBtn={getPriBtnName(formStep)}
				priBtnOnClick={(e) => {
					if (formStep === 2) {
						submit(e);
					} else if (showAlert && formStep === 1) {
						setFormStep(formStep + 1);
					} else {
						priBtnOnClick();
					}
				}}
				priBtnShowIcon={false}
				secBtnOnClick={() => handleBackBtn(formStep)}
				priBtnDisabled={formStep === 1 ? (typeof firstStepNoErrors !== 'undefined' && !firstStepNoErrors ? true : false) : false}
				secBtn={formStep === 3 ? false : 'Cancel'}
				secBtnShowIcon={false}
				classes={cn('max-w-eis-1440', !isEIS && 'border-t border-formOutline mx-6 md:mx-10 lg:mx-14 xl:mx-eis-72 px-0 pt-8 pb-12')}
				{...(!isEIS && { wrapperClasses: 'relative border-t-0 max-w-eis-1440 mx-auto' })}
			/>
		</WrapComponent>
	);
}
